import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';

/* ─── Interfaces ─────────────────────────────────────────── */

interface Invoice {
  id: number;
  invoice_number: string;
  client_name: string;
  customer_id: number;
  total: number;
  amount: number;
  paid_amount: number;
  status: string;
  due_date: string;
  created_at: string;
  credit_term?: string;
  notes?: string;
}

interface AgingRow {
  customer: string;
  customer_id: number;
  current: number;
  days_1_30: number;
  days_31_60: number;
  days_61_90: number;
  days_90_plus: number;
  total: number;
}

interface AgingTotals {
  current: number;
  days_1_30: number;
  days_31_60: number;
  days_61_90: number;
  days_90_plus: number;
  total: number;
}

type TabKey = 'all' | 'overdue' | 'due_soon' | 'pending' | 'paid';

const fmt = (n: number) =>
  '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ─── Component ──────────────────────────────────────────── */

export const AccountsReceivableDashboard: React.FC = () => {
  const { token } = useAuth();

  // ── Invoice state ──────────────────────────────────────
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // ── Aging state ────────────────────────────────────────
  const [agingRows, setAgingRows] = useState<AgingRow[]>([]);
  const [agingTotals, setAgingTotals] = useState<AgingTotals | null>(null);
  const [agingLoading, setAgingLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState<string | null>(null);

  /* ── Helpers ───────────────────────────────────────────── */
  const getDaysOverdue = (inv: Invoice): number => {
    if (inv.status === 'Paid') return 0;
    const due = new Date(inv.due_date);
    if (isNaN(due.getTime())) return 0;
    return Math.floor((Date.now() - due.getTime()) / 86400000);
  };

  const getStatusLabel = (inv: Invoice): string => {
    if (inv.status === 'Paid') return 'Paid';
    const days = getDaysOverdue(inv);
    if (days > 0) return 'Overdue';
    const dueDate = new Date(inv.due_date);
    const daysUntil = Math.ceil((dueDate.getTime() - Date.now()) / 86400000);
    if (daysUntil <= 7) return 'Due Soon';
    return 'Pending';
  };

  const statusStyle = (label: string): { bg: string; color: string } => {
    const map: Record<string, { bg: string; color: string }> = {
      Overdue: { bg: '#fff1f0', color: '#cf1322' },
      'Due Soon': { bg: '#fffbe6', color: '#d46b08' },
      Pending: { bg: '#e6f7ff', color: '#096dd9' },
      Paid: { bg: '#f6ffed', color: '#389e0d' },
    };
    return map[label] || { bg: '#fafafa', color: '#666' };
  };

  /* ── Fetch invoices ────────────────────────────────────── */
  const fetchInvoices = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const normalized: Invoice[] = (Array.isArray(data) ? data : []).map((inv: any) => ({
          id: inv.id,
          invoice_number: inv.invoice_number || inv.invoiceNumber || `INV-${inv.id}`,
          client_name: inv.client_name || inv.clientName || '',
          customer_id: inv.customer_id || inv.customerId || 0,
          total: parseFloat(inv.total ?? inv.amount ?? 0),
          amount: parseFloat(inv.amount ?? inv.total ?? 0),
          paid_amount: parseFloat(inv.paid_amount || inv.paidAmount || 0),
          status: inv.status || '',
          due_date: inv.due_date || inv.dueDate || '',
          created_at: inv.created_at || inv.createdAt || '',
          credit_term: inv.credit_term || inv.creditTerm || '',
          notes: inv.notes || '',
        }));
        setInvoices(normalized);
      }
    } catch (err) {
      console.error('Failed to load invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  /* ── Fetch aging ───────────────────────────────────────── */
  const fetchAging = useCallback(async () => {
    if (!token) return;
    setAgingLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/invoices/reports/aging`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const report = Array.isArray(data.report) ? data.report : [];
        const rows: AgingRow[] = report.map((r: any) => ({
          customer: r.customer_name || r.customer || '',
          customer_id: r.customer_id || 0,
          current: parseFloat(r.bucket_current || 0),
          days_1_30: parseFloat(r.bucket_1_30 || 0),
          days_31_60: parseFloat(r.bucket_31_60 || 0),
          days_61_90: parseFloat(r.bucket_61_90 || 0),
          days_90_plus: parseFloat(r.bucket_90_plus || 0),
          total: parseFloat(r.total_owed || 0),
        }));
        setAgingRows(rows);
        if (data.totals) {
          setAgingTotals({
            current: parseFloat(data.totals.current || 0),
            days_1_30: parseFloat(data.totals.b1_30 || 0),
            days_31_60: parseFloat(data.totals.b31_60 || 0),
            days_61_90: parseFloat(data.totals.b61_90 || 0),
            days_90_plus: parseFloat(data.totals.b90_plus || 0),
            total: parseFloat(data.totals.total || 0),
          });
        } else {
          setAgingTotals({
            current: rows.reduce((s, r) => s + (r.current || 0), 0),
            days_1_30: rows.reduce((s, r) => s + (r.days_1_30 || 0), 0),
            days_31_60: rows.reduce((s, r) => s + (r.days_31_60 || 0), 0),
            days_61_90: rows.reduce((s, r) => s + (r.days_61_90 || 0), 0),
            days_90_plus: rows.reduce((s, r) => s + (r.days_90_plus || 0), 0),
            total: rows.reduce((s, r) => s + (r.total || 0), 0),
          });
        }
      }
    } catch (err) {
      console.error('Failed to load aging data:', err);
    } finally {
      setAgingLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvoices();
    fetchAging();
  }, [fetchInvoices, fetchAging]);

  /* ── Derived stats — all use REMAINING (total - paid_amount) ─── */
  const remaining = (i: Invoice) => Math.max(0, (i.total || i.amount || 0) - (i.paid_amount || 0));
  const stats = {
    totalRemaining: invoices.filter(i => i.status !== 'Paid').reduce((s, i) => s + remaining(i), 0),
    overdue: invoices.filter(i => getStatusLabel(i) === 'Overdue').reduce((s, i) => s + remaining(i), 0),
    dueSoon: invoices.filter(i => getStatusLabel(i) === 'Due Soon').reduce((s, i) => s + remaining(i), 0),
    collected: invoices.reduce((s, i) => s + (i.paid_amount || 0), 0),
    paidCount: invoices.filter(i => i.status === 'Paid').length,
    unpaid: invoices.filter(i => i.status !== 'Paid').length,
  };

  /* ── Filtered invoices ─────────────────────────────────── */
  const filtered = invoices.filter(inv => {
    const label = getStatusLabel(inv);
    if (tab === 'overdue') return label === 'Overdue';
    if (tab === 'due_soon') return label === 'Due Soon';
    if (tab === 'pending') return label === 'Pending';
    if (tab === 'paid') return label === 'Paid';
    return true;
  });

  /* ── Bulk actions ──────────────────────────────────────── */
  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(i => i.id)));
    }
  };
  const sendBulkReminder = () => {
    const names = invoices
      .filter(i => selected.has(i.id))
      .map(i => i.client_name)
      .join(', ');
    showToast(`Sending reminders to: ${names}`);
    setSelected(new Set());
  };

  /* ── Export CSV ────────────────────────────────────────── */
  const exportCSV = () => {
    const headers = ['Invoice #', 'Customer', 'Amount', 'Due Date', 'Status', 'Days Overdue'];
    const rows = filtered.map(inv => [
      inv.invoice_number,
      inv.client_name,
      (inv.total || inv.amount || 0).toFixed(2),
      inv.due_date,
      getStatusLabel(inv),
      getDaysOverdue(inv),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'accounts-receivable.csv';
    a.click();
  };

  /* ── Aging bucket filter ───────────────────────────────── */
  const agingBuckets = [
    { key: 'current', label: 'Current', value: agingTotals?.current || 0, color: '#52c41a' },
    { key: '1_30', label: '1–30 days', value: agingTotals?.days_1_30 || 0, color: '#faad14' },
    { key: '31_60', label: '31–60 days', value: agingTotals?.days_31_60 || 0, color: '#fa8c16' },
    { key: '61_90', label: '61–90 days', value: agingTotals?.days_61_90 || 0, color: '#f5222d' },
    { key: '90_plus', label: '90+ days', value: agingTotals?.days_90_plus || 0, color: '#820014' },
    { key: 'total', label: 'Total', value: agingTotals?.total || 0, color: '#1890ff' },
  ];

  const filteredAgingRows = activeBucket
    ? agingRows.filter(r => {
        if (activeBucket === 'current') return (r.current || 0) > 0;
        if (activeBucket === '1_30') return (r.days_1_30 || 0) > 0;
        if (activeBucket === '31_60') return (r.days_31_60 || 0) > 0;
        if (activeBucket === '61_90') return (r.days_61_90 || 0) > 0;
        if (activeBucket === '90_plus') return (r.days_90_plus || 0) > 0;
        return true;
      })
    : agingRows;

  /* ── Render ────────────────────────────────────────────── */
  return (
    <div style={{ fontFamily: 'Arial, sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 28 }}>📡 Radar Accounts Receivable</h2>
          <p style={{ color: '#666', margin: '4px 0 0 0' }}>Live radar of remaining balances, collections &amp; overdue risk</p>
        </div>
      </div>

      {/* Summary Cards — Radar view */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, marginBottom: 28 }}>
        {[
          { label: '📡 Still Owed (Remaining)', value: stats.totalRemaining, bg: '#f0f5ff', border: '#adc6ff', color: '#1d39c4', sub: `${stats.unpaid} unpaid invoice${stats.unpaid !== 1 ? 's' : ''}` },
          { label: '🔴 Overdue Remaining', value: stats.overdue, bg: '#fff1f0', border: '#ffa39e', color: '#cf1322', sub: 'past due date' },
          { label: '⏰ Due Soon Remaining', value: stats.dueSoon, bg: '#fffbe6', border: '#ffe58f', color: '#d46b08', sub: 'within 7 days' },
          { label: '💳 Collected So Far', value: stats.collected, bg: '#f6ffed', border: '#b7eb8f', color: '#389e0d', sub: `${stats.paidCount} fully paid` },
        ].map(card => (
          <div key={card.label} style={{ background: card.bg, border: `1px solid ${card.border}`, borderRadius: 10, padding: 20 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{card.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: card.color }}>{fmt(card.value)}</div>
            <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{card.sub}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid #f0f0f0', paddingBottom: 0 }}>
        {(['all', 'overdue', 'due_soon', 'pending', 'paid'] as TabKey[]).map(t => {
          const labels: Record<TabKey, string> = { all: 'All', overdue: 'Overdue', due_soon: 'Due Soon', pending: 'Pending', paid: 'Paid' };
          const active = tab === t;
          return (
            <button
              key={t}
              onClick={() => { setTab(t); setSelected(new Set()); }}
              style={{
                padding: '10px 20px',
                background: 'none',
                color: active ? '#1890ff' : '#666',
                border: 'none',
                borderBottom: active ? '2px solid #1890ff' : '2px solid transparent',
                marginBottom: -2,
                fontWeight: active ? 700 : 400,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              {labels[t]}
            </button>
          );
        })}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 8, padding: '10px 16px', marginBottom: 12 }}>
          <span style={{ fontWeight: 600 }}>{selected.size} selected</span>
          <button
            onClick={sendBulkReminder}
            style={{ padding: '6px 14px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
          >
            📧 Send Reminders
          </button>
          <button
            onClick={() => setSelected(new Set())}
            style={{ padding: '6px 14px', background: '#fff', color: '#666', border: '1px solid #d9d9d9', borderRadius: 6, cursor: 'pointer' }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Invoice Table */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e8e8e8', overflow: 'hidden', marginBottom: 36 }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>Loading invoices…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>No invoices for this filter.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                {['Invoice #', 'Customer', 'Total', 'Remaining', 'Due Date', 'Days Overdue', 'Status'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: (h === 'Total' || h === 'Remaining') ? 'right' : 'left', fontWeight: 600, color: '#555', fontSize: 13 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => {
                const label = getStatusLabel(inv);
                const style = statusStyle(label);
                const days = getDaysOverdue(inv);
                const rem = Math.max(0, (inv.total || inv.amount || 0) - (inv.paid_amount || 0));
                return (
                  <tr key={inv.id} style={{ borderBottom: '1px solid #f5f5f5', background: '#fff' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>{inv.invoice_number}</td>
                    <td style={{ padding: '12px 16px' }}>{inv.client_name}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#888' }}>{fmt(inv.total || inv.amount || 0)}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: rem === 0 ? '#389e0d' : '#1d39c4' }}>{fmt(rem)}</td>
                    <td style={{ padding: '12px 16px' }}>{inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-GB') : '—'}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {days > 0 ? (
                        <span style={{ fontWeight: 700, color: days > 60 ? '#820014' : days > 30 ? '#cf1322' : '#d46b08' }}>
                          {days}d
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '4px 12px', background: style.bg, color: style.color, borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                        {label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Aging Breakdown ────────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e8e8e8', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>📈 Aging Breakdown</h3>
          {activeBucket && (
            <button
              onClick={() => setActiveBucket(null)}
              style={{ padding: '6px 14px', background: '#fff', color: '#666', border: '1px solid #d9d9d9', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
            >
              ✕ Clear filter
            </button>
          )}
        </div>

        {/* Bucket Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
          {agingBuckets.map(b => (
            <div
              key={b.key}
              onClick={() => setActiveBucket(activeBucket === b.key ? null : b.key)}
              style={{
                padding: 16,
                borderRadius: 8,
                border: `2px solid ${activeBucket === b.key ? b.color : '#e8e8e8'}`,
                background: activeBucket === b.key ? b.color + '15' : '#fafafa',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{b.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: b.color }}>{fmt(b.value)}</div>
            </div>
          ))}
        </div>

        {/* Per-customer aging table */}
        {agingLoading ? (
          <div style={{ textAlign: 'center', color: '#999', padding: 24 }}>Loading aging data…</div>
        ) : filteredAgingRows.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', padding: 24 }}>No aging data available.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                  {['Customer', 'Current', '1–30 days', '31–60 days', '61–90 days', '90+ days', 'Total'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Customer' ? 'left' : 'right', fontWeight: 600, color: '#555' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAgingRows.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{row.customer}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#52c41a' }}>{fmt(row.current)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#faad14' }}>{fmt(row.days_1_30)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#fa8c16' }}>{fmt(row.days_31_60)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#f5222d' }}>{fmt(row.days_61_90)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#820014', fontWeight: 600 }}>{fmt(row.days_90_plus)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700 }}>{fmt(row.total)}</td>
                  </tr>
                ))}
              </tbody>
              {agingTotals && (
                <tfoot>
                  <tr style={{ background: '#f0f5ff', borderTop: '2px solid #adc6ff', fontWeight: 700 }}>
                    <td style={{ padding: '10px 14px' }}>Total</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#52c41a' }}>{fmt(agingTotals.current)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#faad14' }}>{fmt(agingTotals.days_1_30)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#fa8c16' }}>{fmt(agingTotals.days_31_60)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#f5222d' }}>{fmt(agingTotals.days_61_90)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#820014' }}>{fmt(agingTotals.days_90_plus)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>{fmt(agingTotals.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        <div style={{ marginTop: 16, fontSize: 12, color: '#999', display: 'flex', gap: 16 }}>
          <span>ℹ️ Aging is calculated from invoice due date.</span>
          <span>Click a bucket to filter the customer table.</span>
        </div>
      </div>

    </div>
  );
};

export default AccountsReceivableDashboard;
